import Papa from 'papaparse';

// File signature validation
export const checkFileSignature = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            const buffer = event.target.result;
            const view = new Uint8Array(buffer);
            
            if (view.length >= 4) {
                if (view[0] === 0x50 && view[1] === 0x4B && 
                    view[2] === 0x03 && view[3] === 0x04) {
                    resolve(false); // ZIP/XLSX
                    return;
                }
                
                if (view[0] === 0x25 && view[1] === 0x50 && 
                    view[2] === 0x44 && view[3] === 0x46) {
                    resolve(false); // PDF
                    return;
                }
            }
            
            if (view.length >= 8) {
                if (view[0] === 0xD0 && view[1] === 0xCF && 
                    view[2] === 0x11 && view[3] === 0xE0 && 
                    view[4] === 0xA1 && view[5] === 0xB1 && 
                    view[6] === 0x1A && view[7] === 0xE1) {
                    resolve(false); // Old Office
                    return;
                }
            }
            
            resolve(true);
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file.slice(0, 1024));
    });
};

export const isEnglishOnlyText = (text) => {
    // Remove common punctuation, whitespace, and numbers to focus on actual characters
    const cleaned = text.replace(/[\d\s.,;:"'’\-_=+!?@#$%^&*()<>/\\[\]{}|`~]/g, '');

    // Match anything outside the basic Latin range
    const nonEnglishRegex = /[\u{80}-\u{10FFFF}]/u;

    return !nonEnglishRegex.test(cleaned); // true means all English
};

export const validateLanguage = (content) => {
    const contentLength = content.length;
    const sampleSize = Math.min(contentLength, 5000);

    let sample = '';
    if (contentLength <= sampleSize) {
        sample = content;
    } else {
        const chunkSize = Math.floor(sampleSize / 3);
        sample = content.substring(0, chunkSize) +
                 content.substring(Math.floor(contentLength / 2), Math.floor(contentLength / 2) + chunkSize) +
                 content.substring(contentLength - chunkSize);
    }

    if (!isEnglishOnlyText(sample)) {
        return {
            isValid: false,
            reason: 'File contains non-English characters. Only English content is allowed.'
        };
    }

    return { isValid: true };
};

// Main validation function - optimized flow
export const validateCSVFile = async (file) => {
    const errors = {};
    
    // Step 1: Quick file signature check
    try {
        const signatureValid = await checkFileSignature(file);
        if (!signatureValid) {
            errors.type = 'File appears to be an Excel file or other non-CSV format.';
            return errors;
        }
    } catch (error) {
        errors.type = 'Unable to validate file format.';
        return errors;
    }
    
    // Step 2: Read file content once and validate everything
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target.result;
            
            // Basic empty check
            if (!content || content.trim().length === 0) {
                errors.type = 'File appears to be empty.';
                resolve(errors);
                return;
            }
            
            // Step 3: Language validation (fast check first)
            const languageCheck = validateLanguage(content);
            if (!languageCheck.isValid) {
                errors.type = 'File contains non-English text. Only English content is allowed in CSV files.';
                resolve(errors);
                return;
            }
            
            // Step 4: Papa.parse validation (comprehensive CSV check)
            Papa.parse(content, {
                skipEmptyLines: 'greedy',
                delimitersToGuess: [',', ';', '\t', '|'],
                dynamicTyping: false,
                preview: 100, // Increased for better validation
                complete: (results) => {
                    // Check for critical parsing errors
                    if (results.errors && results.errors.length > 0) {
                        const criticalErrors = results.errors.filter(error => 
                            error.type === 'Delimiter' || 
                            error.type === 'FieldMismatch' ||
                            error.code === 'UndetectableDelimiter'
                        );
                        
                        // If too many critical errors, it's likely not a valid CSV
                        if (criticalErrors.length > results.data.length * 0.2) {
                            errors.type = 'File contains invalid CSV structure or formatting.';
                            resolve(errors);
                            return;
                        }
                    }

                    // Check if we have actual data
                    if (!results.data || results.data.length === 0) {
                        errors.type = 'File contains no valid data rows.';
                        resolve(errors);
                        return;
                    }
                    
                    // Check for minimum data requirement (headers + at least one data row)
                    const validRows = results.data.filter(row => 
                        row && row.length > 0 && 
                        row.some(cell => cell != null && cell.toString().trim() !== '')
                    );
                    
                    if (validRows.length < 1) {
                        errors.type = 'File must contain at least one valid data row.';
                        resolve(errors);
                        return;
                    }
                    
                    // Column structure validation
                    const firstRowLength = validRows[0].length;
                    if (firstRowLength < 2) { // Your original requirement
                        errors.type = 'CSV must have at least 2 columns.';
                        resolve(errors);
                        return;
                    }
                    
                    // Column consistency check
                    const consistentRows = validRows.filter(row => 
                        Math.abs(row.length - firstRowLength) <= 1 // Allow 1 column difference
                    );
                    
                    if (consistentRows.length < validRows.length * 0.8) { // 80% consistency
                        errors.type = 'File has inconsistent column structure across rows.';
                        resolve(errors);
                        return;
                    }
                    
                    // All validations passed
                    resolve(errors); // Empty errors object means validation passed
                },
                error: (parseError) => {
                    errors.type = 'Unable to parse file as valid CSV format.';
                    resolve(errors);
                }
            });
        };
        
        reader.onerror = () => {
            errors.type = 'Unable to read file content.';
            resolve(errors);
        };
        
        reader.readAsText(file);
    });
};